/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutViewProps,
    IEmailDelivery,
    IInvoicePaymentSummary,
    ILineItem,
    ILineItemDeliveryGroup,
    IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout';
import { ICheckoutProps as ICheckoutExtensionProps } from '../definition-extensions/checkout.ext.props.autogenerated';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

const getProcessingFeeTotalFromCheckoutLineItems = (lineItems: ICheckoutViewProps['lineItems']): number => {
    const allLineItems = [
        ...(lineItems?.itemsForShip?.lineItems ?? []),
        ...(lineItems?.itemsForPickup?.lineItems ?? []),
        ...(lineItems?.itemsForEmail?.lineItems ?? [])
    ];

    return allLineItems.reduce((total, lineItem) => {
        const itemElement = lineItem.item;

        if (!React.isValidElement(itemElement)) {
            return total;
        }

        const itemProps = itemElement.props as {
            data?: {
                cartLine?: {
                    ChargeLines?: Array<{
                        Description?: string;
                        ChargeCode?: string;
                        CalculatedAmount?: number;
                    }>;
                };
            };
        };

        const chargeLines = itemProps.data?.cartLine?.ChargeLines ?? [];

        const processingFeeForLine = chargeLines.reduce((sum, chargeLine) => {
            return sum + Number(chargeLine.CalculatedAmount ?? 0);
        }, 0);

        return total + processingFeeForLine;
    }, 0);
};

const getOtherChargeTotal = (lines: IOrderSummary['lines']): number => {
    const otherCharge = lines?.otherCharge;

    if (React.isValidElement(otherCharge)) {
        const props = otherCharge.props as { price?: number };
        return Number(props.price ?? 0);
    }

    return 0;
};

const getOrderTotal = (lines: IOrderSummary['lines']): number => {
    const orderTotal = lines?.orderTotal;

    if (React.isValidElement(orderTotal)) {
        const props = orderTotal.props as { price?: number };
        return Number(props.price ?? 0);
    }

    return 0;
};

const getTaxTotal = (lines: IOrderSummary['lines']): number => {
    const tax = lines?.tax;

    if (React.isValidElement(tax)) {
        const props = tax.props as { price?: number };
        return Number(props.price ?? 0);
    }

    return 0;
};

const getSubTotal = (lines: IOrderSummary['lines']): number => {
    const subTotal = lines?.subtotal;

    if (React.isValidElement(subTotal)) {
        const props = subTotal.props as { price?: number };
        return Number(props.price ?? 0);
    }

    return 0;
};

const getLocaleFromCultureFormatter = (cultureFormatter: unknown): string | undefined => {
    return (cultureFormatter as { locale?: string } | undefined)?.locale;
};

const formatCheckoutCurrency = (cultureFormatter: unknown, currencyCode: string | undefined, amount: number): string => {
    const locale = getLocaleFromCultureFormatter(cultureFormatter);
    const currency = currencyCode ?? 'CHF';

    if (locale === 'fr-CH') {
        const formatter = cultureFormatter as { formatCurrency?: (price: number, currencyCode?: string) => string } | undefined;

        return formatter?.formatCurrency ? formatter.formatCurrency(amount, currency) : `${amount.toFixed(2)} ${currency}`;
    }

    return `${currency} ${amount.toFixed(2)}`;
};

const OrderSummaryComponent: React.FC<IOrderSummary & {
    checkoutLineItems: ICheckoutViewProps['lineItems'];
    cultureFormatter?: unknown;
    currencyCode?: string;
    resources: ICheckoutExtensionProps<{}>['resources'];
    cartTotal?: number;
}> = ({ heading, lines, checkoutLineItems, cultureFormatter, currencyCode, resources, cartTotal }) => {
    const processingFeeTotal = getProcessingFeeTotalFromCheckoutLineItems(checkoutLineItems);
    const otherChargeTotal = getOtherChargeTotal(lines);
    const shippingCostTotal = otherChargeTotal - processingFeeTotal;
    const orderTotal = Number(cartTotal ?? getOrderTotal(lines));
    const taxTotal = getTaxTotal(lines);

    const formatPrice = (amount: number): string => {
        return formatCheckoutCurrency(cultureFormatter, currencyCode, amount);
    };

    const hasProcessingFee = Math.abs(processingFeeTotal) > 0.001;
    const hasShippingCosts = Math.abs(shippingCostTotal) > 0.001;
    const subTotal = getSubTotal(lines);

    return (
        <div className='msc-order-summary-wrapper'>
            {heading}
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        <p className='msc-order-summary__line-other-charges'>
                            <span className='msc-order-summary__label'>{resources.subTotalLabel}</span>
                            <span className='msc-price msc-order-summary__value'>
                                <span className='msc-price__actual'>{formatPrice(subTotal)}</span>
                            </span>
                        </p>

                        {hasProcessingFee && (
                            <p className='msc-order-summary__line-other-charges'>
                                <span className='msc-order-summary__label'>{resources.surchargesAndDiscountsLabel}</span>
                                <span className='msc-price msc-order-summary__value'>
                                    <span className='msc-price__actual'>{formatPrice(processingFeeTotal)}</span>
                                </span>
                            </p>
                        )}

                        <p className='msc-order-summary__line-other-charges'>
                            <span className='msc-order-summary__label'>{resources.shippingCostsLabel}</span>
                            <span className='msc-price msc-order-summary__value'>
                                <span className='msc-price__actual'>{hasShippingCosts ? formatPrice(shippingCostTotal) : '-'}</span>
                            </span>
                        </p>

                        <p className='msc-order-summary__line-other-charges'>
                            <span className='msc-order-summary__label'>{resources.checkoutTaxLabel}</span>
                            <span className='msc-price msc-order-summary__value'>
                                <span className='msc-price__actual'>{formatPrice(taxTotal)}</span>
                            </span>
                        </p>
                        {lines.loyalty}
                        {lines.giftCard}
                        <p className='msc-order-summary__line-total'>
                            <span className='msc-order-summary__label'>{resources.orderTotalLabel}</span>
                            <span className='msc-price msc-order-summary__value'>
                                <span className='msc-price__actual'>{formatPrice(orderTotal)}</span>
                            </span>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutExtensionProps<{}>> = (
    props: ICheckoutViewProps & ICheckoutExtensionProps<{}>
) => {
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        placeOrderButton,
        termsAndConditions,
        checkoutExpressPaymentContainer,
        checkoutErrorRef,
        lineItems,
        resources
    } = props;

    console.log(props);

    return (
        <Module {...checkoutProps} ref={checkoutErrorRef}>
            {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && isPaymentVerificationRedirection && shouldEnableSinglePaymentAuthorizationCheckout && !alert && !loading && (
                <Waiting className='msc-waiting-circular msc-waiting-lg' />
            )}
            {alert}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {checkoutExpressPaymentContainer}
                                {checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                                {guidedForm}
                                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                <Node {...mainControlProps}>{placeOrderButton}</Node>
                            </Node>
                            <Node {...sideProps}>
                                {!hasInvoiceLine
                                    ? orderSummary && (
                                          <OrderSummaryComponent
                                              {...orderSummary}
                                              checkoutLineItems={lineItems}
                                              cultureFormatter={props.context.cultureFormatter}
                                              currencyCode={
                                                  ((props.context.cultureFormatter as unknown) as { currencyCode?: string }).currencyCode
                                              }
                                              resources={resources}
                                              cartTotal={props.data.checkout.result?.checkoutCart.cart.TotalAmount}
                                          />
                                      )
                                    : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
